import {
	DeleteOutlined,
	DownOutlined,
	FileSyncOutlined,
	PlusOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import {
	Alert,
	Button,
	Card,
	ConfigProvider,
	Dropdown,
	Empty,
	Modal,
	notification,
	Select,
	Space,
	TableProps,
	Tag,
} from 'antd';
import Search from 'antd/es/input/Search';
import { ColumnsType, SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import type { HTMLAttributes } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Link, useSearchParams } from 'react-router-dom';
import '../../customTable.css';
import {
	MaterialRequest,
	MaterialTypeObject,
} from '../../interfaces/Material.Interface';
import {
	useAddMaterialOrderDraftMutation,
	useGetMaterialVendorsQuery,
} from '../../redux/api/material';
import { useHasMultipleBuildings } from '../../hooks/useHasMultipleBuildings';
import {
	useDeleteMaterialRequestMutation,
	useLazyGetMaterialRequestQuery,
	useUpdateMaterialRequestMutation,
} from '../../redux/api/material-request';

import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { THEME } from '../../main.tsx';
import { useLazyGetOrderLineItemByIdQuery } from '../../redux/api/order.ts';
import { showMaterialRequestModal } from '../../redux/slices/material-request.ts';
import { getOrderLineItemStatusTagColor } from '../../utils';
import CustomResponsiveTable from '../CustomResponsiveTable/index.tsx';
import MaterialRequestModal from '../MaterialRequestModal';
import RequestAdditionalMaterialModal from '../RequestAdditionalMaterialModal';
interface MaterialRequestTableProps {
	hiddenMaterialRequests?: boolean;
	onReadyChange?: (ready: boolean) => void;
}

const MaterialRequestTable = (props: MaterialRequestTableProps) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const hasMultipleBuildings = useHasMultipleBuildings();
	const [selectedRowKeys, setSelectedRowKeys] = useState<Array<string>>([]);
	const [searchParams, setSearchParams] = useSearchParams();
	const [getLazyMaterial] = useLazyGetMaterialRequestQuery();
	const [materialRequests, setMaterialRequest] = useState<any[]>([]);
	const [enable, setEnable] = useState(false);
	const { search } = useLocation();
	const [find, setFind] = useState<any>();
	const [sort, setSort] = useState<any>();
	const [limit, setLimit] = useState(1);
	const [count, setCount] = useState(0);
	const [load, setLoad] = useState(false);
	const [hasLoaded, setHasLoaded] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedRequests, setSelectedRequests] = useState<any[]>([]);
	const [addMaterialOrderDraft] = useAddMaterialOrderDraftMutation();
	const [updateMaterialRequestMutation] = useUpdateMaterialRequestMutation();
	const [deleteMaterialRequest] = useDeleteMaterialRequestMutation();
	const [selectedMaterialRequest, setSelectedMaterialRequest] = useState<any>();
	const [isRequestMoreMaterialVisible, setIsRequestMoreMaterialVisible] =
		useState(false);

	const queue = searchParams.get('queue') || undefined;
	const vendor = searchParams.get('vendor') || undefined;
	const building = searchParams.get('building') || undefined;
	const [searchVendor, setSearchVendor] = useState<string | undefined>(
		undefined
	);

	const vendorsQuery = useGetMaterialVendorsQuery({ search: searchVendor });
	const [fetchOrderLineItem, { data, isLoading, error }] =
		useLazyGetOrderLineItemByIdQuery();

	const onChange: TableProps<MaterialTypeObject>['onChange'] = (
		_pagination,
		_filters,
		sorter,
		extra
	) => {
		const sorterColumn = (sorter as SorterResult<MaterialTypeObject>)?.column;

		if (extra.action === 'sort' && !sorterColumn) {
			setSort('');
			const page = limit;
			const filter = find;
			const object: any = {};
			if (filter) {
				object['find'] = find;
			}
			if (page) {
				object['page'] = limit;
			}

			setSearchParams({ ...object });
		}
		if (extra.action === 'sort' && sorterColumn) {
			const orderType = (sorter as SorterResult<MaterialTypeObject>)?.order;

			const column =
				orderType === 'ascend' || orderType === undefined
					? sorterColumn?.key
					: '-' + sorterColumn?.key;

			if (column) {
				setSort(() => column);
				const filter = find;
				const page = limit;
				const object: any = {};
				if (filter) {
					object['find'] = find;
				}
				if (page) {
					object['page'] = limit;
				}
				setSearchParams({ ordering: column, ...object });
			}
		}
	};

	const onSearch = (value: string) => {
		if (value != '') {
			setFind(() => value);
			const page = limit;
			const order = sort;
			const object: any = {};
			if (order) {
				object['ordering'] = order;
			}
			if (page) {
				object['page'] = 1;
			}
			if (queue) {
				object['queue'] = queue;
			}
			if (vendor) {
				object['vendor'] = vendor;
			}
			if (building) {
				object['building'] = building;
			}

			setSearchParams({ search: value, ...object });
		}
		if (value == '') {
			setFind(() => '');
			const page = limit;
			const order = sort;
			const object: any = {};
			if (order) {
				object['ordering'] = order;
			}
			if (page) {
				object['page'] = limit;
			}
			if (queue) {
				object['queue'] = queue;
			}
			if (vendor) {
				object['vendor'] = vendor;
			}
			if (building) {
				object['building'] = building;
			}
			setSearchParams({ ...object });
		}
	};

	const showModal = () => {
		setIsModalOpen(true);
	};

	const handleOk = async () => {
		const selectedRows = selectedRequests.map((item) => {
			return {
				vendor: item.vendor_material.vendor.id,
				type: item.vendor_material.type.id,
				shape: item.vendor_material.shape.id,
				quantity_minimum: 0,
				quantity_ordered: 0,
				material_order_request: item.id,
				length: item.total_required_length,
				status: 'Draft',
				vendor_part_number: item.vendor_material.vendor_part_number,
			};
		});

		await addMaterialOrderDraft({
			material_order_items: selectedRows,
		})
			.unwrap()
			.then((res) => {
				navigate(`/material/orders/add/${res.id}`);
			})
			.catch((error) => {
				console.log(error.data);
				setIsModalOpen(false);
				notification.error({
					duration: 10,
					message: 'Error',
					description: JSON.stringify(error.data),
					placement: 'topRight',
				});
			});
	};

	const handleCancel = () => {
		setIsModalOpen(false);
	};

	useEffect(() => {
		GetAsyncMaterialRequested();
	}, [search]);

	async function GetAsyncMaterialRequested() {
		const object: any = {};

		const getMaterialUUIDFromQueryString = searchParams.get('search');

		if (sort) {
			object['ordering'] = sort;
		}

		const page = searchParams.get('page');

		if (page) {
			object['page'] = page;
		}

		if (find) {
			object['search'] = find;
		}

		if (queue) {
			object['queue'] = queue;
		}

		if (vendor) {
			object['vendor'] = vendor;
		}

		if (building) {
			object['building'] = building;
		}

		if (!find && getMaterialUUIDFromQueryString) {
			object['search'] = getMaterialUUIDFromQueryString;
		}

		if (props.hiddenMaterialRequests) {
			object['status'] = 'Submitted';
		}

		setLoad(true);
		const response = await getLazyMaterial({ ...object });
		setLoad(false);
		setHasLoaded(true);
		setCount(() => (response.data?.count ? response.data.count : 0));
		setMaterialRequest(response.data ? response.data.results : []);

		if (
			getMaterialUUIDFromQueryString &&
			response.data &&
			response.data.results.length > 0
		) {
			const material_request = response.data.results.find(
				(item: MaterialTypeObject) => item.id == getMaterialUUIDFromQueryString
			);

			if (material_request && material_request.status === 'Submitted') {
				setEnable(true);
				setSelectedRequests([material_request]);

				setSelectedRowKeys([getMaterialUUIDFromQueryString]);
			} else {
				setEnable(false);
			}
		}
	}

	const tableColumns: ColumnsType<any> = [
		{
			title: 'Vendor',
			key: 'vendor__display_name',
			width: 150,
			render: (record: MaterialRequest) =>
				record.vendor_material?.vendor?.display_name,
			sorter: true,
		},
		{
			title: 'Part #',
			key: 'vendor_part_number',
			width: 125,
			render: (record: MaterialRequest) =>
				record.vendor_material?.vendor_part_number,
			sorter: true,
		},
		{
			title: 'Type',
			key: 'type__display_name',
			width: 150,
			render: (record: MaterialRequest) =>
				record?.vendor_material?.type?.display_name,
			sorter: true,
		},
		{
			title: 'Shape',
			key: 'shape__display_name',
			width: 150,
			render: (record: MaterialRequest) =>
				`${record?.vendor_material?.shape?.display_name}`,
			sorter: true,
		},
		{
			title: 'Size',
			width: 150,
			render: (record: MaterialRequest) => record?.display_size,
			sorter: false,
		},
		{
			title: 'Cut Length / Part Length',
			key: 'cut_length',
			width: 150,
			render: (record: MaterialRequest) => `${record?.material_cut_length}`,
			sorter: true,
		},
		{
			title: 'Total Length',
			key: 'length',
			width: 150,
			render: (record: MaterialRequest) => `${record?.total_required_length}`,
			sorter: true,
		},
		...(hasMultipleBuildings
		? [
			{
				title: 'Building',
				key: 'building',
				width: 150,
				render: (record: MaterialRequest) =>
					record.building?.name ?? '—',
			},
		  ]
		: []),
		{
			title: 'Requested For',
			key: 'sale_order_name',
			width: 250,
			render: (record: MaterialRequest) => (
				<>
					<div>
						<Link
							to={`/orders/${record.order_line_item?.order?.id}`}
							type="link"
							className="flex justify-start truncate"
							style={{ textAlign: 'start', width: '100%', padding: 0 }}
						>
							{record.order_line_item?.order?.name}
						</Link>
						<Link
							to={`/orders/line-items/${record.order_line_item?.id}`}
							type="link"
							className="flex justify-start truncate"
							style={{ textAlign: 'start', width: '100%', padding: 0 }}
						>
							{record.order_line_item?.name}
						</Link>
					</div>
					{record.order_line_item &&
					record.order_line_item.cancelled_quantity > 0 &&
					record.order_line_item.status !== 'Cancelled' ? (
						<Tag color={getOrderLineItemStatusTagColor('Quantity Changed')}>
							<WarningOutlined style={{ fontSize: '0.7rem' }} /> Quantity
							Changed
						</Tag>
					) : (
						record.order_line_item &&
						record.order_line_item.status !== 'In Progress' &&
						record.order_line_item.status !== 'Completed' &&
						record.order_line_item.status !== 'Received' && (
							<Tag
								color={getOrderLineItemStatusTagColor(
									record.order_line_item.status
								)}
							>
								{record.order_line_item?.status}
							</Tag>
						)
					)}
				</>
			),
			sorter: false,
		},
		{
			title: 'Status',
			key: 'status',
			width: 150,
			render: (record: MaterialRequest) => (
				<>
					<span
						data-testid="fastlane-material-request-status"
						data-material-request-id={record.id}
						data-material-request-status={record?.status || ''}
					>
						{record?.status}
					</span>
					{record?.material && record.material?.material_order && (
						<Button
							onClick={() =>
								navigate(`/material/orders/${record.material?.material_order}`)
							}
							type="link"
							className="flex justify-start truncate"
							style={{
								// width: '150px',
								fontSize: '13px',
								padding: 0,
							}}
						>
							View Material Order
						</Button>
					)}
				</>
			),
			sorter: true,
		},
		{
			title: 'Additional Request Reason',
			key: 'is_additional_request',
			width: 250,
			render: (record: MaterialRequest) => (
				<>
					{record && record.additional_request_reason && (
						<span>
							{typeof record.additional_request_reason === 'object'
								? record.additional_request_reason.name
								: record.additional_request_reason}
						</span>
					)}
				</>
			),
			sorter: true,
		},
		{
			title: 'Created by',
			width: 150,
			render: (record: MaterialRequest) => (
				<div
					style={{
						width: '130px',
						textOverflow: 'ellipsis',
						overflowX: 'hidden',
						textAlign: 'left',
						whiteSpace: 'nowrap',
					}}
				>
					<span>{record?.created_by}</span>
				</div>
			),
			sorter: false,
		},
		{
			title: 'Created Date',
			width: 150,
			key: 'created_time',
			render: (record: MaterialRequest) =>
				dayjs(record?.created_time).format('MMM DD YYYY'),
			sorter: true,
		},
		{
			title: 'Actions',
			width: 150,
			render: (record: MaterialRequest) => (
				<Space>
					<Button.Group>
						<Button
							disabled={['Ordered', 'Reserved'].includes(record.status)}
							onClick={async () => {
								if (typeof record.order_line_item === 'object') {
									dispatch(showMaterialRequestModal());
									const lineItemId = record.order_line_item.id;
									await fetchOrderLineItem({ id: lineItemId });
									setSelectedMaterialRequest(record);
								}
							}}
						>
							Edit
						</Button>

						<Dropdown
							menu={{
								items: [
									{
										label: 'Request More',
										key: '1',
										icon: <PlusOutlined />,
										onClick: () => {
											setSelectedMaterialRequest(record);
											setIsRequestMoreMaterialVisible(true);
										},
									},
									{
										label: 'Delete',
										key: '2',
										icon: <DeleteOutlined />,
										disabled: ['Ordered', 'Reserved'].includes(record.status),
										onClick: () => {
											Modal.confirm({
												title: 'Delete Material Request?',
												centered: true,
												icon: null,
												onOk: async () => {
													await deleteMaterialRequest({
														id: record.id,
													})
														.unwrap()
														.then(() => GetAsyncMaterialRequested());
												},
												footer: (_, { OkBtn, CancelBtn }) => (
													<ConfigProvider theme={THEME}>
														<CancelBtn />
														<OkBtn />
													</ConfigProvider>
												),
											});
										},
									},
								],
							}}
						>
							<Button icon={<DownOutlined />} />
						</Dropdown>
					</Button.Group>
				</Space>
				// <Dropdown.Button
				// 	disabled={['Ordered', 'Reserved'].includes(record.status)}
				// 	onClick={async () => {
				// 		if (typeof record.order_line_item === 'object') {
				// 			dispatch(showMaterialRequestModal());
				// 			const lineItemId = record.order_line_item.id;
				// 			await fetchOrderLineItem({ id: lineItemId });
				// 			setSelectedMaterialRequest(record);
				// 		}
				// 	}}
				// 	menu={{
				// 		items: [
				// 			{
				// 				label: 'Request More',
				// 				key: '1',
				// 				icon: <PlusOutlined />,
				// 				onClick: () => {
				// 					setSelectedMaterialRequest(record);
				// 					setIsRequestMoreMaterialVisible(true);
				// 				},
				// 			},
				// 			{
				// 				label: 'Delete',
				// 				key: '2',
				// 				icon: <DeleteOutlined />,
				// 				onClick: () => {
				// 					Modal.confirm({
				// 						title: 'Delete Material Request?',
				// 						centered: true,
				// 						icon: null,
				// 						onOk: async () => {
				// 							await deleteMaterialRequest({
				// 								id: record.id,
				// 							})
				// 								.unwrap()
				// 								.then(() => GetAsyncMaterialRequested());
				// 						},
				// 						footer: (_, { OkBtn, CancelBtn }) => (
				// 							<ConfigProvider theme={THEME}>
				// 								<CancelBtn />
				// 								<OkBtn />
				// 							</ConfigProvider>
				// 						),
				// 					});
				// 				},
				// 			},
				// 		],
				// 	}}
				// >
				// 	Edit
				// </Dropdown.Button>
			),
		},
	];

	const selectedBuildingId =
		selectedRequests.length > 0
			? selectedRequests[0]?.building?.id ?? null
			: undefined;

	const rowSelection = {
		onChange: (selectedKeys: any, selectedRows: any) => {
			const dif = selectedKeys;
			setSelectedRequests(() => selectedRows);
			if (selectedRows.length > 0 && dif.length > 0) {
				setEnable(true);
			} else {
				setEnable(false);
			}

			setSelectedRowKeys(selectedKeys);
		},
		selectedRowKeys,
		getCheckboxProps: (record: MaterialTypeObject) => {
			const isStatusDisabled =
				record.status === 'Ordered' || record.status === 'Reserved';

			const isBuildingMismatch =
				hasMultipleBuildings &&
				selectedBuildingId !== undefined &&
				(record.building?.id ?? null) !== selectedBuildingId;

			return {
				name: record.status,
				disabled: isStatusDisabled || isBuildingMismatch,
			};
		},
	};

	const emptyTable = () => {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					<span style={{ color: '#000' }}>
						There are currently no materials to be displayed.
					</span>
				}
			/>
		);
	};

	const debounceVendor = useCallback(
		_.debounce((search: string) => {
			setSearchVendor(search);
		}, 500),
		[]
	);

	const tableReady = hasLoaded && !load;

	useEffect(() => {
		props.onReadyChange?.(tableReady);
	}, [props.onReadyChange, tableReady]);

	return (
		<>
			<Modal
				title="Convert to Material Order"
				centered={true}
				open={isModalOpen}
				onOk={handleOk}
				okText="Submit"
				onCancel={handleCancel}
			>
				<p>
					Would you like to create a new material order based on the selected
					Material Requests?
				</p>
			</Modal>
			<div className="flex flex-col md:flex-row">
				<div className="search-input">
					<Search
						placeholder="Search"
						allowClear
						onSearch={onSearch}
						style={{ width: '100%' }}
					/>
				</div>
				<div className={'mx-2'}>
					<Select
						value={vendor}
						showSearch={true}
						onSearch={debounceVendor}
						onSelect={(value) => {
							const object: any = {};
							object['page'] = 1;
							if (queue) {
								object['queue'] = queue;
							}
							if (searchParams.get('search')) {
								object['search'] = searchParams.get('search');
							}
							if (building) {
								object['building'] = building;
							}
							setSearchParams({ vendor: value, ...object });
						}}
						placeholder={'Select Vendor'}
						style={{ width: 250 }}
						loading={vendorsQuery.isFetching}
						filterOption={false}
					>
						{vendorsQuery.data?.map((vendor) => (
							<Select.Option key={vendor.id} value={vendor.id}>
								{vendor.display_name}
							</Select.Option>
						))}
					</Select>
				</div>
				<div className={'mx-2'}>
					<Button
						onClick={() => {
							const queue = searchParams.get('queue');
							const nextParams: Record<string, string> = {};
							if (queue) {
								nextParams['queue'] = queue;
							}
							if (building) {
								nextParams['building'] = building;
							}
							setSearchParams(nextParams);
							setFind('');
							setSort('');
						}}
						type="default"
					>
						Reset
					</Button>
				</div>
				<Button
					className="md:ml-2"
					onClick={showModal}
					disabled={!enable}
					icon={<FileSyncOutlined />}
				>
					Convert to Material Order
				</Button>
			</div>

			{hasMultipleBuildings && selectedRequests.length > 0 && (
				<Alert
					className="mt-2"
					type="info"
					showIcon
					message={`Only requests from "${selectedRequests[0]?.building?.name ?? 'No Building'}" can be selected. Clear your selection to choose a different building.`}
				/>
			)}

			<Card className="shadow-sm" styles={{ body: { padding: 0 } }}>
				<div
					data-testid="fastlane-material-requests-table"
					data-ready={tableReady ? 'true' : 'false'}
				>
				<CustomResponsiveTable
					columns={tableColumns}
					rowSelection={rowSelection}
					data={materialRequests}
					excludeWidthColumn={true}
					loading={load}
					rowKey="id"
					onRow={(record) => ({
						'data-testid': 'fastlane-material-request-row',
						'data-material-request-id': record.id,
					} as HTMLAttributes<HTMLElement>)}
					onChange={onChange}
					emptyTable={emptyTable}
					pagination={
						count
							? {
									pageSize: 20,
									pageSizeOptions: [],
									total: count,
									current: Number(searchParams.get('page')) || 1,
									showSizeChanger: false,
									onChange: (page) => {
										const filter = find;
										const sorting = sort;
										const object: any = {};
										if (filter) {
											object['find'] = find;
										}
										if (sorting) {
											object['ordering'] = sorting;
										}
										if (queue) {
											object['queue'] = queue;
										}
										if (vendor) {
											object['vendor'] = vendor;
										}
										if (building) {
											object['building'] = building;
										}
										setLimit(page);
										setSearchParams({ page: page.toString(), ...object });
									},
									defaultPageSize: 20,
							  }
							: false
					}
				/>
				</div>
			</Card>

			<RequestAdditionalMaterialModal
				visible={isRequestMoreMaterialVisible}
				setVisible={setIsRequestMoreMaterialVisible}
				material_request={selectedMaterialRequest}
				refetchData={GetAsyncMaterialRequested}
			/>

			<MaterialRequestModal
				lineItem={data}
				materialRequest={selectedMaterialRequest}
				refetchData={GetAsyncMaterialRequested}
				onClose={() => setSelectedMaterialRequest(null)}
			/>
		</>
	);
};

export default MaterialRequestTable;
